import type { ComponentProps, ReactNode } from 'react';

import styled from '@emotion/styled';
import { Box, Button, PressableBox, Text } from '@opendoor/bricks-next';
import { IconSearch } from '@tabler/icons-react';
import { SocialProofBox } from 'components/landing-pages-v2/SocialProofBox';
import NewSellerFlowAddressInput from 'components/shared/NewSellerFlowAddressInput';

import { isTreatmentAnalyticsId } from '../header/glass/constants';
import { useHeroContext } from './HeroShared';

type TextColor = ComponentProps<typeof Text>['color'];

interface HeroTitleAndAddressProps {
  analyticsId?: string;
  title: string;
  subtitle?: string;
  showAddressInput?: boolean;
  showSocialSection?: boolean;
  renderDesktopInput?: () => ReactNode;
  renderMobileInput?: () => ReactNode;
  additionalAddressContent?: ReactNode;
  addressEntryRef: (node?: Element | null) => void;
  // Default input props (used when renderDesktopInput/renderMobileInput are not provided)
  addressCtaText?: string;
  addressCtaTextSmall?: string;
  addressCtaPlaceholderText?: string;
  addressCtaPlaceholderTextSmall?: string;
  trackingKeyword?: string;
  // CTA buttons container props
  showCtaButtonsContainer?: boolean;
  ctaButton1Text?: string;
  ctaButton1Url?: string;
  showCtaButton2?: boolean;
  ctaButton2Text?: string;
  ctaButton2Url?: string;
  textColor?: TextColor;
}

// Internal component: HeroHeadline
interface HeroHeadlineProps {
  title: string;
  subtitle?: string;
  isTreatment?: boolean;
  textColor?: TextColor;
}

const HeroHeadline = ({ title, subtitle, isTreatment, textColor }: HeroHeadlineProps) => {
  return (
    <Box
      flexDirection="column"
      gap="$8"
      alignItems="center"
      $largerThanSM={isTreatment ? { alignItems: 'flex-start', mb: '$4' } : undefined}
      $largerThanMD={isTreatment ? undefined : { alignItems: 'flex-start' }}
    >
      <Text
        tag="h1"
        typography="$displayXsmall"
        textAlign="center"
        {...(textColor ? { color: textColor } : {})}
        $largerThanSM={isTreatment ? { textAlign: 'left' } : { typography: '$displaySmall' }}
        $largerThanMD={isTreatment ? {} : { typography: '$displayMedium', textAlign: 'left' }}
        style={isTreatment ? { lineHeight: '80%' } : {}}
      >
        {title}
      </Text>
      {subtitle && (
        <Text
          tag="p"
          typography="$bodyMedium"
          textAlign="center"
          {...(textColor ? { color: textColor } : {})}
          $largerThanSM={
            isTreatment
              ? { typography: '$bodyLarge', textAlign: 'left' }
              : { typography: '$bodyLarge' }
          }
          $largerThanMD={isTreatment ? undefined : { textAlign: 'left' }}
        >
          {subtitle}
        </Text>
      )}
    </Box>
  );
};

// Internal component: HeroAddressSection
interface HeroAddressSectionProps {
  showAddressInput?: boolean;
  renderDesktopInput: () => ReactNode;
  renderMobileInput: () => ReactNode;
  additionalContent?: ReactNode;
  isTreatment: boolean;
  textColor?: TextColor;
}

const HeroAddressSection = ({
  showAddressInput,
  renderDesktopInput,
  renderMobileInput,
  isTreatment,
  textColor,
}: HeroAddressSectionProps) => {
  if (!showAddressInput) return null;

  return (
    <Box
      width="100%"
      zIndex={10}
      className="hp-reskin-v2-address-entry-wrapper"
      $largerThanSM={isTreatment ? { marginBottom: '$4' } : undefined}
    >
      <Box id="desktop-address-search" display="none" $largerThanLG={{ display: 'block' }}>
        {renderDesktopInput()}
      </Box>
      <Box id="mobile-address-search" display="block" $largerThanLG={{ display: 'none' }}>
        {renderMobileInput()}
      </Box>
      {isTreatment && (
        <Text
          typography="$bodyMedium"
          tag="p"
          color={textColor || '$contentSecondary'}
          textAlign="center"
          marginTop="$4"
          $largerThanSM={{ textAlign: 'left' }}
        >
          No obligation. Takes 5 minutes. Your information stays private.
        </Text>
      )}
    </Box>
  );
};

// Internal component: HeroCtaButtonsSection
interface HeroCtaButtonsSectionProps {
  ctaButton1Text?: string;
  ctaButton1Url?: string;
  showCtaButton2?: boolean;
  ctaButton2Text?: string;
  ctaButton2Url?: string;
  analyticsPrefix: string;
}

const HeroCtaButtonsSection = ({
  ctaButton1Text = 'Missing label',
  ctaButton1Url,
  showCtaButton2,
  ctaButton2Text = 'Missing label',
  ctaButton2Url,
  analyticsPrefix,
}: HeroCtaButtonsSectionProps) => {
  return (
    <Box
      flexDirection="column"
      gap="$6"
      alignItems="center"
      $largerThanMD={{
        flexDirection: 'row',
        alignItems: 'flex-start',
      }}
    >
      <Button
        analyticsName={`${analyticsPrefix}-cta-button-1`}
        label={ctaButton1Text}
        size="large"
        variant="primary"
        href={ctaButton1Url}
      />
      {showCtaButton2 && (
        <Button
          analyticsName={`${analyticsPrefix}-cta-button-2`}
          label={ctaButton2Text}
          size="large"
          variant="secondary"
          href={ctaButton2Url}
        />
      )}
    </Box>
  );
};

const addressInputCommonProps = (analyticsId?: string, trackingKeyword?: string) => ({
  hideLabel: true,
  inputLocation: 'hero-cta' as const,
  analyticsPrefix: `cosmos-landing-page-hero-v2${analyticsId ? `-${analyticsId}` : ''}`,
  trackingTaxonomy: 'new_homepage' as const,
  trackingKeywords: trackingKeyword,
});

const TreatmentSearchButton = ({
  analyticsPrefix,
  onPress,
  isLoading,
}: {
  analyticsPrefix: string;
  onPress?: () => void;
  isLoading?: boolean;
}) => (
  <PressableBox
    disabled={isLoading}
    analyticsName={analyticsPrefix}
    aria-label="Search"
    display="flex"
    alignItems="center"
    justifyContent="center"
    width="$15"
    height="$16"
    borderRadius="$9x"
    backgroundColor="$backgroundAccentBrand"
    cursor="pointer"
    opacity={isLoading ? 0.7 : 1}
    $platform-web={{ marginTop: '-20px', marginRight: '-16px' }}
    onPress={(e: any) => {
      e?.stopPropagation?.();
      onPress?.();
    }}
  >
    <IconSearch size={24} color="white" stroke={2} />
  </PressableBox>
);

const StyledDefaultActionButton = styled(Button)`
  height: 60px !important;
  margin-top: -17.5px;
  margin-right: -15px;
`;

const DefaultActionButton = ({
  label,
  analyticsPrefix,
  onPress,
  isLoading,
}: {
  label: string;
  analyticsPrefix: string;
  onPress?: () => void;
  isLoading?: boolean;
}) => (
  <StyledDefaultActionButton
    disabled={isLoading}
    analyticsName={analyticsPrefix}
    label={isLoading ? 'Loading' : label}
    size="medium"
    variant="primary"
    onPress={(e: any) => {
      e?.stopPropagation?.();
      onPress?.();
    }}
  />
);

export const HeroTitleAndAddress = ({
  analyticsId,
  title,
  subtitle,
  showAddressInput,
  showSocialSection,
  renderDesktopInput,
  renderMobileInput,
  additionalAddressContent,
  addressEntryRef,
  addressCtaText = 'Get cash offer',
  addressCtaTextSmall = 'Get offer',
  addressCtaPlaceholderText,
  addressCtaPlaceholderTextSmall,
  trackingKeyword,
  showCtaButtonsContainer,
  ctaButton1Text,
  ctaButton1Url,
  showCtaButton2,
  ctaButton2Text,
  ctaButton2Url,
  textColor,
}: HeroTitleAndAddressProps) => {
  const { analyticsPrefix } = useHeroContext();

  const isTreatment = isTreatmentAnalyticsId(analyticsId);
  const aiCommonProps = addressInputCommonProps(analyticsId, trackingKeyword);

  const DefaultInput = ({ placeholder, ctaLabel }: { placeholder: string; ctaLabel: string }) => (
    <NewSellerFlowAddressInput
      {...aiCommonProps}
      placeholder={placeholder}
      actionButton={(onPress, isLoading) => (
        <DefaultActionButton
          analyticsPrefix={analyticsPrefix}
          label={ctaLabel}
          onPress={onPress}
          isLoading={isLoading}
        />
      )}
    />
  );
  const TreatmentInput = ({ placeholder }: { placeholder: string }) => (
    <NewSellerFlowAddressInput
      {...aiCommonProps}
      placeholder={placeholder}
      actionButton={(onPress, isLoading) => (
        <TreatmentSearchButton
          analyticsPrefix={analyticsPrefix}
          onPress={onPress}
          isLoading={isLoading}
        />
      )}
    />
  );

  const desktopInput = (): ReactNode => {
    if (isTreatment) {
      return (
        <TreatmentInput placeholder={addressCtaPlaceholderText || 'Enter your home address'} />
      );
    }
    if (renderDesktopInput) {
      return renderDesktopInput();
    }
    return (
      <DefaultInput
        placeholder={addressCtaPlaceholderText || 'Enter your home address'}
        ctaLabel={addressCtaText}
      />
    );
  };
  const mobileInput = (): ReactNode => {
    if (isTreatment) {
      return <TreatmentInput placeholder={addressCtaPlaceholderTextSmall || 'Enter an address'} />;
    }
    if (renderMobileInput) {
      return renderMobileInput();
    }
    return (
      <DefaultInput
        placeholder={addressCtaPlaceholderTextSmall || 'Enter an address'}
        ctaLabel={addressCtaTextSmall}
      />
    );
  };

  return (
    <Box
      id="title-and-address-entry"
      flexDirection="column"
      width="100%"
      gap={isTreatment ? '$4' : '$8'}
      $largerThanSM={{
        gap: isTreatment ? '$0' : '$8',
      }}
      $largerThanLG={{ gap: isTreatment ? '$0' : '$8' }}
    >
      <HeroHeadline
        title={title}
        subtitle={subtitle}
        isTreatment={isTreatment}
        textColor={textColor}
      />
      <Box display="none" ref={(el) => addressEntryRef(el as HTMLElement)} />
      <Box
        display="flex"
        flexDirection="column"
        alignItems={isTreatment ? 'flex-start' : 'center'}
        gap="$4"
        width="100%"
      >
        <HeroAddressSection
          showAddressInput={showAddressInput}
          renderDesktopInput={desktopInput}
          renderMobileInput={mobileInput}
          additionalContent={additionalAddressContent}
          isTreatment={isTreatment}
          textColor={textColor}
        />
        <Box
          $smallerThanMD={{ display: 'none' }}
          $smallerThanLG={{ display: 'inherit' }}
          $largerThanMD={{ display: 'none' }}
          width="100%"
        >
          {showSocialSection && <SocialProofBox autoplayInterval={3000} />}
        </Box>
      </Box>
      {showCtaButtonsContainer && (
        <HeroCtaButtonsSection
          ctaButton1Text={ctaButton1Text}
          ctaButton1Url={ctaButton1Url}
          showCtaButton2={showCtaButton2}
          ctaButton2Text={ctaButton2Text}
          ctaButton2Url={ctaButton2Url}
          analyticsPrefix={analyticsPrefix}
        />
      )}
    </Box>
  );
};
